/**
 * Wire schemas for the Operations **Accepted book** surface — the bridge stage
 * between the Under Review book and the Placed book ("the land").
 *
 * A lead that a Handling Firm has **Accepted** has left the Under Review book
 * (the routing-bloat view drains it at its Accepted line) but has not yet
 * become a case: it is *on the line*. The firm is signing the client and
 * chasing the client-signed **FSA**; when the FSA returns, the placement
 * flips to **Placed** and the catch is landed. Accepted book is the surface for
 * that in-between — the leads hooked but not yet landed.
 *
 * Scope: **both unsigned leads and Transfers** that are awaiting the FSA.
 * Both cross this bridge — the FSA (the fee-split instrument) must be
 * executed before any placement flips to Placed, signed Transfer or not.
 * The surface keeps the two populations apart: an **Unsigned Leads** tab and
 * a **Transfers** tab, split on `signed` (inherited from `LeadSchema`), each
 * sorted worst-wait-first. The wire payload is one flat sorted array; the UI
 * partitions it by `signed`, so this schema needs no group field.
 *
 * The surface answers the two questions Chris asks of an accepted lead:
 *   1. How long has it been sitting here? → `daysAwaitingFsa`, the dwell
 *      clock since the firm accepted. The worst-first sort key, and not
 *      gameable (it anchors on the Accepted moment, which a firm can't
 *      reset by re-touching the record).
 *   2. Is it being pushed, or stalling? → `lastOutreachDays`, lifted
 *      intact from the Under Review book's outreach-recency read (ADR-0043):
 *      days since the firm last logged client outreach. Recent = the firm
 *      is reeling it in; stale = the line's gone slack. SHOWN, never
 *      sorted on — outreach is firm self-report (the ADR-0043 caveat).
 *
 * Simpler than the Under Review book by construction: an Accepted lead is
 * committed to ONE firm, so there is no custody bar, no multi-firm
 * trajectory chain, no LegalFlare reroute gaps, and no tolling — just one
 * firm, one clock, one outreach read.
 *
 * All derivation is read-only and live — no stored Accepted book state.
 *
 * Spec: engineering/command-center/docs/port-notes/fishing.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import { LeadSchema } from "./lead.js";

/**
 * One lead on the line — the typed Lead plus the resolved OF name, the
 * accepting firm, the dwell clock, and the outreach read. All joins and
 * derivations are done by the composer so the UI renders without its own
 * lookups.
 */
export const AcceptedLeadSchema = LeadSchema.extend({
  /** Joined `Firm Name` for `originatingFirmId`, or null. Orientation. */
  originatingFirmName: z.string().nullable(),

  /** The Handling Firm that accepted — the one signing the client and
   *  owing the FSA. Resolved from the Accepted placement's firm link, or
   *  null when the lookup missed. */
  currentFirmName: z.string().nullable(),

  /** The Accepted placement's record id — the target an operator action
   *  (decline) writes to. Always present: an Accepted book lead has an Accepted
   *  placement by the on-the-line filter. */
  acceptedPlacementId: z.string(),

  /** SURFACE — days since the firm accepted (the Accepted placement's
   *  `decisionDate`, fallback `dateResponded`, fallback lead creation).
   *  When the FSA wait actually began. The worst-first sort key; clamped
   *  ≥ 0. Not gameable — it anchors on the Accepted moment. */
  daysAwaitingFsa: z.number(),

  /** ISO-8601 datetime the firm accepted, or null. Backs the "accepted
   *  {date}" label in the expanded row. */
  acceptedDate: z.string().nullable(),

  /** SURFACE — days since the accepting firm last logged client outreach
   *  (the newest `[<ISO>]` entry in its Outreach Log). The "is the line
   *  still moving?" read: an old-by-the-clock catch with recent outreach
   *  is being reeled in, not stalling. Null when the firm has logged
   *  nothing yet (itself a tell — surfaced in the UI as "no outreach
   *  logged"). SHOWN, never sorted on (ADR-0043): outreach is firm
   *  self-report, and ranking on it would let a firm sink a real stall
   *  with one token line. Clamped ≥ 0. */
  lastOutreachDays: z.number().nullable(),

  /** The accepting firm's append-only outreach log (multilineText), or
   *  null. The evidence the operator reads on expand to judge whether the
   *  firm is actually working the signature. */
  outreachLog: z.string().nullable(),
});
export type AcceptedLead = z.infer<typeof AcceptedLeadSchema>;

/**
 * Payload for `GET /api/accepted`. Already filtered to the on-the-line
 * population (Accepted, not yet Placed, not Withdrawn) and sorted worst-first
 * (highest `daysAwaitingFsa`). Carries both unsigned leads and Transfers;
 * the UI tabs split them on `signed`.
 */
export const AcceptedResponseSchema = z.object({
  leads: z.array(AcceptedLeadSchema),
});
export type AcceptedResponse = z.infer<typeof AcceptedResponseSchema>;
